import styled from "styled-components/macro";
import { TextField } from "../shared/TextField/TextField";
import { Password } from "../shared/TextField/Password";
import { DeviceType } from "../../utils/hooks/useDeviceHook";

const formFieldWidth = (deviceType: string) => {
  if (deviceType === "desktop") return "453px";
  return "100%";
};

export const LoginFormColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  width: 100%;
  max-width: 520px;
`;

export const LoginHeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  text-align: center;
  width: 100%;
  max-width: 453px;
`;

export const LoginFieldsSection = styled.div<{ deviceType: DeviceType }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 24px;
  width: ${({ deviceType }) => formFieldWidth(deviceType)};
`;

export const CenterContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  place-items: center;
  margin: auto;
  grid-template-rows: min-content min-content min-content;
`;

export const FormFieldInput = styled.div<{
  deviceType: DeviceType;
}>`
  width: ${({ deviceType }) => formFieldWidth(deviceType)};
`;

export const LoginInput = styled(TextField)`
  width: 100%;
  && {
    .MuiOutlinedInput-root {
      border-radius: 4px;
      background-color: ${({ theme }) => theme.white};
    }
    .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme }) => theme.greyLight};
    }
    .MuiOutlinedInput-input {
      padding: 16px 12px;
      font-size: 16px;
      line-height: 24px;
      color: ${({ theme }) => theme.greyMedium};
    }
    .MuiInputLabel-root {
      color: ${({ theme }) => theme.greyDark};
      font-size: 12px;
    }
    .MuiInputLabel-asterisk {
      color: ${({ theme }) => theme.redDark};
    }
  }
`;

export const LoginErrorWrapper = styled(FormFieldInput)`
  margin-bottom: 20px;
  text-align: center;

  a {
    color: inherit;
    text-decoration: underline;
    font-weight: 600;
  }
`;

export const LoginPassword = styled(Password)`
  width: 100%;
  && {
    .MuiOutlinedInput-root {
      border-radius: 4px;
      background-color: ${({ theme }) => theme.white};
    }
    .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme }) => theme.greyLight};
    }
    .MuiOutlinedInput-input {
      padding: 16px 12px;
      font-size: 16px;
      line-height: 24px;
      color: ${({ theme }) => theme.greyMedium};
    }
    .MuiInputLabel-root {
      color: ${({ theme }) => theme.greyDark};
      font-size: 12px;
    }
    .MuiInputLabel-asterisk {
      color: ${({ theme }) => theme.redDark};
    }
    .MuiIconButton-root {
      color: ${({ theme }) => theme.greyLight};
    }
  }
`;

export const ButtonContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  place-items: center;
  margin-top: 35px;
`;

export const PasswordLinkContainer = styled.div<{
  deviceType: DeviceType;
}>`
  display: flex;
  justify-content: flex-end;
  width: ${({ deviceType }) => formFieldWidth(deviceType)};
  text-align: end;
`;

export const DesktopContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  background-color: white;
`;

export const NonDesktopShell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  background-color: ${({ theme }) => theme.white};
`;

export const LoginHeroBand = styled.div<{ $deviceType: DeviceType }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  gap: ${({ $deviceType }) => ($deviceType === "mobile" ? "12px" : "24px")};
  padding-left: ${({ $deviceType }) =>
    $deviceType === "mobile" ? "16px" : "24px"};
  padding-right: ${({ $deviceType }) =>
    $deviceType === "mobile" ? "16px" : "24px"};
  padding-bottom: ${({ $deviceType }) =>
    $deviceType === "mobile" ? "12px" : "24px"};
  padding-top: ${({ $deviceType }) =>
    $deviceType === "mobile"
      ? "calc(32px + env(safe-area-inset-top, 0px))"
      : "calc(40px + env(safe-area-inset-top, 0px))"};
  background: ${({ theme, $deviceType }) =>
    $deviceType === "mobile"
      ? theme.loginHeroGradientMobile
      : theme.loginHeroGradient};
`;

export const NonDesktopContents = styled.div<{ $deviceType: DeviceType }>`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  padding-top: ${({ $deviceType }) =>
    $deviceType === "mobile" ? "32px" : "80px"};
  padding-left: ${({ $deviceType }) =>
    $deviceType === "tablet" ? "clamp(24px, 12vw, 96px)" : "16px"};
  padding-right: ${({ $deviceType }) =>
    $deviceType === "tablet" ? "clamp(24px, 12vw, 96px)" : "16px"};
  padding-bottom: calc(48px + env(safe-area-inset-bottom, 0px));
`;

export const DesktopColorBackground = styled.div`
  position: relative;
  min-height: 100vh;
  height: 100%;
  overflow: hidden;
  background-color: ${({ theme }) => theme.loginBrandPanel};
`;

export const DesktopLoginGradientLayer = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ theme }) => theme.gradient};
  transform: rotate(180deg) scaleX(-1);
  transform-origin: center center;
`;

export const DesktopBrandPanel = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  z-index: 1;
  padding: 0 40px 0 clamp(24px, 4vw, 55px);
  box-sizing: border-box;
  pointer-events: none;

  & img {
    pointer-events: auto;
  }
`;

export const DesktopBrandRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 16px;
  width: 100%;
`;

export const DesktopBrandTitleAside = styled.div`
  margin-left: auto;
  display: flex;
  align-items: flex-end;
  min-width: 0;
  padding-left: 10px;
`;

export const DesktopBrandLockup = styled.img`
  display: block;
  height: 93px;
  width: auto;
  max-width: min(228px, 42vw);
  flex-shrink: 0;
`;

export const LoginHeroLockupImg = styled.img<{ $deviceType: DeviceType }>`
  display: block;
  height: ${({ $deviceType }) => ($deviceType === "mobile" ? "28px" : "40px")};
  width: auto;
  max-width: 100%;
`;

export const DesktopBrandTitleImg = styled.img`
  display: inline-block;
  vertical-align: baseline;
  height: 12px;
  width: auto;
  max-width: min(167px, 36vw);
`;

export const LoginHeroTitleImg = styled.img<{ $deviceType: DeviceType }>`
  display: block;
  height: ${({ $deviceType }) => ($deviceType === "mobile" ? "11px" : "13px")};
  width: auto;
  max-width: min(200px, 72vw);
`;

export const LoginPrivacyPolicy = styled.div<{
  deviceType: DeviceType;
}>`
  display: flex;
  justify-content: center;
  margin: 0;
  text-align: center;
  width: ${({ deviceType }) => formFieldWidth(deviceType)};
  max-width: 448px;

  p {
    font-size: 14px;
    line-height: 23px;
    color: ${({ theme }) => theme.neutral700};
    margin: 0;
  }

  a {
    color: inherit;
    text-decoration: underline;
    text-decoration-skip-ink: none;
  }
`;

export const StatusBannerWrapper = styled.div<{
  deviceType: DeviceType;
}>`
  width: ${({ deviceType }) => formFieldWidth(deviceType)};
  margin-bottom: ${({ deviceType }) =>
    deviceType === "mobile" ? "32px" : "56px"};
`;
